<script lang="ts">
	import { browser } from '$app/env';
	import { page } from '$app/state';
	import * as Dialog from '$lib/components/ui/dialog';
	import { Input } from '$lib/components/ui/input/index.js';
	import { Label } from '$lib/components/ui/label/index.js';
	import { Textarea } from '$lib/components/ui/textarea/index.js';
	import * as m from '$lib/paraglide/messages.js';
	import Button from '@/components/ui/button/button.svelte';
	import { currentUser, pb } from '@/utils/ApiClient';
	import LifeBuoy from '@lucide/svelte/icons/life-buoy';
	import posthog from 'posthog-js';
	import { toast } from 'svelte-sonner';

	let { open = $bindable() } = $props();

	let report = $state({
		email: '',
		description: ''
	});

	let isSubmitting = $state(false);
	let imageFiles = $state<FileList | undefined>(undefined);
	let previewObjectUrl = $state<string | null>(null);
	let imageInputKey = $state(0);

	let path = $derived(browser ? page.url.pathname : '');

	const HELP_CENTER_URL = 'https://zarpar.eu/ayuda';

	function onScreenshotInput() {
		const prev = previewObjectUrl;
		const f = imageFiles?.item(0) ?? null;
		previewObjectUrl = f ? URL.createObjectURL(f) : null;
		if (prev) {
			URL.revokeObjectURL(prev);
		}
	}

	function resetScreenshot() {
		const prev = previewObjectUrl;
		previewObjectUrl = null;
		imageFiles = undefined;
		imageInputKey += 1;
		if (prev) {
			URL.revokeObjectURL(prev);
		}
	}

	async function sendReport() {
		if (isSubmitting) return;
		isSubmitting = true;

		const user = $currentUser ? $currentUser.email : 'anonimo';

		const recordingUrl = posthog.get_session_replay_url({
			withTimestamp: true,
			timestampLookBack: 10
		});

		const text = 'Problema de ' + user + ' en ' + path;
		const file = imageFiles?.item(0) ?? null;

		posthog.capture('bug_report_sent', {
			description: report.description,
			user,
			has_image: Boolean(file)
		});

		const formData = new FormData();
		formData.set('text', text);
		formData.set('description', report.description);
		formData.set('email', report.email);
		formData.set('recordingUrl', recordingUrl ?? '');
		if (file) {
			formData.set('image', file, file.name);
		}

		try {
			await pb.send('/report', {
				method: 'POST',
				body: formData
			});

			toast.success('Reporte enviado. Gracias por ayudarnos a mejorar.');
			open = false;
			report.description = '';
			report.email = '';
			resetScreenshot();
		} catch (error) {
			console.error('Error:', error);
			toast.error('Error al enviar el reporte. Inténtalo de nuevo.');
		} finally {
			isSubmitting = false;
		}
	}
</script>

<Dialog.Root bind:open={open}>
	<Dialog.Content class="sm:max-w-[425px]">
		<Dialog.Header>
			<Dialog.Title>Cuéntanos qué ha pasado</Dialog.Title>
		</Dialog.Header>

		<!-- Help center callout -->
		<div
			class="flex flex-col gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/50 dark:bg-orange-950/30"
		>
			<p class="text-sm text-gray-700 dark:text-gray-300">
				¿Has consultado nuestro centro de ayuda? Puede que encuentres la respuesta allí.
			</p>
			<a
				href={HELP_CENTER_URL}
				target="_blank"
				rel="noopener noreferrer"
				class="inline-flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700 dark:text-orange-500 dark:hover:text-orange-400"
			>
				<LifeBuoy class="size-4" />
				{m.patchy_round_cuckoo_stop()}
				<span aria-hidden="true">→</span>
			</a>
		</div>

		<!-- Form -->
		<div class="grid gap-4 py-4">
			<div class="flex w-full flex-col gap-1.5">
				<Label for="description">Describe tu duda o problema</Label>
				<Textarea
					id="description"
					bind:value={report.description}
					placeholder="Escribe aquí"
					rows={4}
					disabled={isSubmitting}
				/>
			</div>
			{#if !$currentUser}
				<div class="flex w-full flex-col gap-1.5">
					<Label for="email">Si quieres que te contestemos, introduce tu email</Label>
					<Input
						id="email"
						bind:value={report.email}
						type="email"
						placeholder="tu@email.com"
						disabled={isSubmitting}
					/>
				</div>
			{/if}
			<div class="flex w-full flex-col gap-1.5">
				<div class="flex items-center justify-between gap-2">
					<Label for="screenshot">Captura (opcional)</Label>
					{#if imageFiles?.length}
						<Button
							type="button"
							variant="ghost"
							size="sm"
							class="h-auto shrink-0 py-1 text-xs"
							onclick={resetScreenshot}
							disabled={isSubmitting}
						>
							Quitar imagen
						</Button>
					{/if}
				</div>
				<p class="text-muted-foreground text-xs">
					Ayuda a reproducir el fallo: una foto de la pantalla o del mensaje de error.
				</p>
				{#key imageInputKey}
					<Input
						id="screenshot"
						type="file"
						accept="image/jpeg,image/png,image/webp,image/gif"
						bind:files={imageFiles}
						disabled={isSubmitting}
						oninput={onScreenshotInput}
					/>
				{/key}
				{#if previewObjectUrl}
					<img
						src={previewObjectUrl}
						alt="Vista previa de la captura que se enviará"
						class="border-input max-h-40 w-full rounded-md border object-contain"
					/>
				{/if}
			</div>
		</div>

		<Dialog.Footer class="sm:justify-center">
			<Button onclick={() => sendReport()} disabled={isSubmitting}>
				{isSubmitting ? 'Enviando...' : 'Enviar reporte'}
			</Button>
		</Dialog.Footer>
	</Dialog.Content>
</Dialog.Root>
